import clsx from 'clsx';
import { JSX, MouseEvent, ReactElement } from 'react';

import { Icon } from '../icon';
import { Alert, type AlertProps } from '../alert';
import { dismissAllPopovers, Popover, PopoverProps } from '../popover';

import styles from './inline-confirmation.module.scss';

export const InlineConfirmation = (props: InlineConfirmationProps) => {
  const {
    open,
    error,
    alert,
    onOpen,
    onClose,
    onConfirm,
    children,
    appearance = 'light',
    closeOnConfirm = true,
    ...rest
  } = props;

  const renderAlert = (triggerElement: ReactElement) => {
    return (
      <Alert
        popoverProps={{
          placement: 'right-start',
          offset: [0, 15, -35],
        }}
        trigger={triggerElement}
        {...alert!}
      />
    );
  };

  return (
    <Popover
      arrow
      open={open}
      onOpen={onOpen}
      offset={[0, 10]}
      arrowSize="small"
      placement="right"
      cornerRadius="smooth"
      closeOnClickOutside={false}
      className={styles.inlineConfirmHolder}
      containerClassName={clsx(styles.inlineConfirmationPopover, styles[`a-${appearance}`])}
      {...rest}
      content={
        <>
          <button onClick={onClose} data-testid="inline-cancel">
            <Icon type="multiplication-sign" size={16} />
          </button>
          {!error && (
            <>
              {!!alert ? (
                renderAlert(
                  <button data-testid="inline-confirm" disabled={error}>
                    <Icon type="tick-02" size={16} />
                  </button>
                )
              ) : (
                <button
                  disabled={error}
                  onClick={(e) => {
                    closeOnConfirm && dismissAllPopovers();
                    onConfirm?.(e);
                  }}
                  data-testid="inline-confirm"
                >
                  <Icon type="tick-02" size={16} />
                </button>
              )}
            </>
          )}
        </>
      }
    >
      {children}
    </Popover>
  );
};

export interface InlineConfirmationProps extends PopoverProps {
  open: boolean;
  error?: boolean;
  alert?: AlertProps;
  onClose?: () => void;
  children: ReactElement;
  closeOnConfirm?: boolean;
  appearance?: 'dark' | 'light';
  onConfirm?: (e: MouseEvent) => void;
  renderAlert?: (triggerElement: ReactElement) => JSX.Element;
}
